import { CampaignStatus, FilterTypes } from "../../../../types";
import { GEO_CONFIRMATION_MESSAGES, validateGeoConfirmation, validateGeorecencyDuration, validateBookedImpressions, validateEndDate, validateFrequency, validateFrequencyFor, validateStartDate, validateDayparts, validateDeviceCaps } from "../../validators";
import { shouldValidateOnLineItem } from "../../validators/generic";
import { DealCategory } from "../../../../models/deals";
import { RemoteConfigProperties } from "../../../../types";
import { validateFlightEndDates, validateFlightStartDates, validateLineItemCreativeFlightEndDatesInRange, validateLineItemCreativeFlightStartDatesInRange } from "../creativeFlights/validators";
import { validateLineItemCreativeFlights } from "./attachedCreatives";
import { validateCircles } from "./geofence";
export * from "./status";
export const LINE_ITEM_FLIGHT_START_DATE_RANGE_ERROR = "Please check the start dates for the creatives.";
export const LINE_ITEM_FLIGHT_END_DATE_RANGE_ERROR = "Please check the end dates for the creatives.";
export function validators(sdk) {
    return {
        adPositions: (lineItem, value) => {
            const isAdPositionEnabled = sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.PRE_MID_POST_ROLL_TARGETING);
            if (!shouldValidateOnLineItem(lineItem, "adPositions") || !isAdPositionEnabled) {
                return undefined;
            }
            if (!value?.length) {
                return "At least 1 ad position is required to proceed.";
            }
            return undefined;
        },
        /**
         * Booked Impressions: Must be a number >= 0 && <= 10MM. Once a line item is created, its impressions cannot be decreased. Cannot have both impressions and budget set simultaneously
         */
        bookedImpressions: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "bookedImpressions")) {
                return undefined;
            }
            return validateBookedImpressions(lineItem, value);
        },
        /**
         * A line item must have a name
         */
        name: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "name")) {
                return undefined;
            }
            if (!value?.trim()?.length) {
                return "Please give the line item a name.";
            }
            return undefined;
        },
        dayparts: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "dayparts")) {
                return undefined;
            }
            return validateDayparts(value);
        },
        deviceCaps: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "deviceCaps")) {
                return undefined;
            }
            return validateDeviceCaps(value);
        },
        frequencies: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "frequencies")) {
                return undefined;
            }
            return validateFrequency(value);
        },
        "frequencies.day": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "frequencies")) {
                return undefined;
            }
            return validateFrequencyFor("DAY", lineItem.frequencies);
        },
        "frequencies.week": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "frequencies")) {
                return undefined;
            }
            return validateFrequencyFor("WEEK", lineItem.frequencies);
        },
        "frequencies.month": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "frequencies")) {
                return undefined;
            }
            return validateFrequencyFor("MONTH", lineItem.frequencies);
        },
        /**
         * A live line item must have a product.
         */
        productId: (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "productId")) {
                return undefined;
            }
            if (typeof value === "string" && value.trim().length) {
                return undefined;
            }
            return "Product is required.";
        },
        /**
         * A live line item must have a publisher group.
         */
        publisherGroupId: async (lineItem, value) => {
            if (!shouldValidateOnLineItem(lineItem, "publisherGroupId")) {
                return undefined;
            }
            if (typeof value === "string" && value.trim().length) {
                return undefined;
            }
            if (lineItem?.productId) {
                const product = await sdk.products?.find({
                    where: [
                        {
                            field: "id",
                            value: lineItem.productId,
                            type: FilterTypes.EQ
                        }
                    ]
                });
                const isLegacyProduct = product?.[0]?.isLegacyProduct !== false;
                // if product is not legacy, we don't validate publisher group
                if (!isLegacyProduct) {
                    return undefined;
                }
            }
            return "Publisher group is required.";
        },
        /**
         * A line item requires a start date (that is before the end date), if it is not set to start ASAP.
         */
        startDate: (lineItem, value, current, updating) => {
            if (!shouldValidateOnLineItem(lineItem, "startDate")) {
                return undefined;
            }
            return validateStartDate(lineItem, value, updating);
        },
        /**
         * A line item requires an end date (that is after the start date) or BE throws an error.
         */
        endDate: (lineItem, value, current, updating) => {
            if (!shouldValidateOnLineItem(lineItem, "endDate")) {
                return undefined;
            }
            return validateEndDate(lineItem, value, updating);
        },
        /**
         * A line item requires one live creative flight to be set live or BE throws an error.
         */
        "attachedCreatives.status": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.status") ||
                !lineItem.attachedCreatives?.length) {
                return undefined;
            }
            if (lineItem.status === CampaignStatus.LIVE &&
                lineItem.attachedCreatives.every((creative) => creative.status !== 200)) {
                return "At least one creative flight needs to be set live before this line item can be set live.";
            }
            return undefined;
        },
        /**
         * A line item requires at least one creative flight to be set live
         */
        "attachedCreatives.empty": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.empty")) {
                return undefined;
            }
            if (!lineItem.attachedCreatives?.length) {
                return "There are no attached creatives on this line item.";
            }
            return undefined;
        },
        /**
         * LineItem.attachedCreatives are individually validated via `MadSDK.campaigns.lineItems.creativeFlights.validators`.
         * The keys prefixed with "attachedCreatives." below, loop through each creative flight on line item,
         * runs the validations for [key] in `attachedCreatives.[key]`, and returns the first error message found.
         */
        /**
         * Tracking pixels must be valid URLs and no duplicates or BE throws an error.
         */
        "attachedCreatives.trackingPixels": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.trackingPixels")) {
                return undefined;
            }
            return validateLineItemCreativeFlights("trackingPixels", lineItem, sdk);
        },
        /**
         * Each creative flight must have a weight or BE throws an error.
         */
        "attachedCreatives.weight": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.weight")) {
                return undefined;
            }
            return validateLineItemCreativeFlights("weight", lineItem, sdk)
                ? "Please enter a weight for all creatives. To pause a creative, use the ... button on the creative row."
                : undefined;
        },
        /**
         * If a creative flight has a click through URL, it must be a valid URL or BE throws an error.
         */
        "attachedCreatives.clickThroughUrl": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.clickThroughUrl")) {
                return undefined;
            }
            return validateLineItemCreativeFlights("clickThroughUrl", lineItem, sdk)
                ? "All clickthrough urls must be valid domains, have https protocol and be url safe."
                : undefined;
        },
        /**
         * All creative flights must have a start date defined, and it must come before the flight end date.
         */
        "attachedCreatives.startDate": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.startDate")) {
                return undefined;
            }
            return validateFlightStartDates(lineItem)
                ? LINE_ITEM_FLIGHT_START_DATE_RANGE_ERROR
                : undefined;
        },
        /**
         * All creative flights must have an end date defined, and it must come after the flight start date.
         */
        "attachedCreatives.endDate": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.endDate")) {
                return undefined;
            }
            return validateFlightEndDates(lineItem) ? LINE_ITEM_FLIGHT_END_DATE_RANGE_ERROR : undefined;
        },
        /**
         * All creative flights must start on or after the line item starts.
         */
        "attachedCreatives.startDatesInRange": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.startDatesInRange")) {
                return undefined;
            }
            return validateLineItemCreativeFlightStartDatesInRange(lineItem)
                ? LINE_ITEM_FLIGHT_START_DATE_RANGE_ERROR
                : undefined;
        },
        /**
         * All creative flights must end on or before the line item ends.
         */
        "attachedCreatives.endDatesInRange": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.endDatesInRange")) {
                return undefined;
            }
            return validateLineItemCreativeFlightEndDatesInRange(lineItem)
                ? LINE_ITEM_FLIGHT_END_DATE_RANGE_ERROR
                : undefined;
        },
        /**
         * Flight name must contain only allowed characters and be within max length if provided.
         */
        "attachedCreatives.flightName": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "attachedCreatives.flightName")) {
                return undefined;
            }
            return validateLineItemCreativeFlights("flightName", lineItem, sdk);
        },
        /**
         * When supply selector is enabled and product is selected, line item must have inventory
         * packages.
         */
        "inventoryPackagesV2.empty": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "inventoryPackagesV2.empty")) {
                return undefined;
            }
            // PG Deal line items don't use Supply Selector (see deals.empty) — stale
            // productId/inventoryPackagesV2 left over from Product mode must not block this.
            if (lineItem.dealCategory === DealCategory.PG_DEAL) {
                return undefined;
            }
            // Only validate if supply selector is enabled for line items
            const isSupplySelectorEnabled = sdk.featureFlags.isSupplySelectorEnabled("line item");
            if (!isSupplySelectorEnabled) {
                return undefined;
            }
            // Only validate if product is selected
            if (!lineItem.productId) {
                return undefined;
            }
            // If inventoryPackagesV2 is undefined, don't show an error (this means no edits
            // have been made to packages yet).
            if (lineItem.inventoryPackagesV2 === undefined) {
                return undefined;
            }
            // Check if inventory packages are empty
            if (!lineItem.inventoryPackagesV2?.length) {
                return "Please add inventory packages or rollups in the supply selector.";
            }
            return undefined;
        },
        /**
         * PG Deal line items must have a deal attached (per the DEL-1560 backend audit, deals
         * attach via inventory packages). See `deals` on LineItemBase for why the selected
         * deal is tracked separately from `inventoryPackagesV2`.
         */
        "deals.empty": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "deals.empty")) {
                return undefined;
            }
            if (lineItem.dealCategory !== DealCategory.PG_DEAL) {
                return undefined;
            }
            if (!lineItem.deals?.id) {
                return "Please select a deal for this PG Deal line item.";
            }
            return undefined;
        },
        /**
         * Geographic targeting validation: Country is required for geofence circles only.
         * Georecency circles do not require a country.
         * Note: This only validates when geoTargeting structure exists, as some
         * organizations or order types may not use geographic targeting.
         */
        geoTargeting: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "geoTargeting")) {
                return undefined;
            }
            // Only validate if geoTargeting structure exists (indicating it's being used)
            if (!lineItem.geoTargeting?.include) {
                return undefined;
            }
            // if any geographic targeting EXCEPT georecency exists, the country is required
            const hasGeofenceCircles = lineItem.geoTargeting.include.circles?.length > 0;
            if (!hasGeofenceCircles) {
                return undefined;
            }
            const country = lineItem.geoTargeting.include.country;
            if (!country || typeof country !== "string" || !country.trim()) {
                return "Country is required for geographic targeting.";
            }
            return undefined;
        },
        /**
         * Geofence circles validation: Circles with validation errors should not be saved.
         * A circle will contain a populated validations array if it is invalid.
         */
        geofence: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "geofence")) {
                return undefined;
            }
            return validateCircles(lineItem.geoTargeting?.include?.circles);
        },
        confirmedRegionalRestrictions: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "confirmedRegionalRestrictions")) {
                return undefined;
            }
            return validateGeoConfirmation(lineItem.geoTargeting?.include?.circles, lineItem.confirmedRegionalRestrictions, GEO_CONFIRMATION_MESSAGES.confirmedRegionalRestrictions);
        },
        confirmedSensitiveCategories: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "confirmedSensitiveCategories")) {
                return undefined;
            }
            return validateGeoConfirmation(lineItem.geoTargeting?.include?.circles, lineItem.confirmedSensitiveCategories, GEO_CONFIRMATION_MESSAGES.confirmedSensitiveCategories);
        },
        confirmedDeliveryImpact: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "confirmedDeliveryImpact")) {
                return undefined;
            }
            return validateGeoConfirmation(lineItem.geoTargeting?.include?.circles, lineItem.confirmedDeliveryImpact, GEO_CONFIRMATION_MESSAGES.confirmedDeliveryImpact);
        },
        "georecency.circles": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "georecency.circles")) {
                return undefined;
            }
            return validateCircles(lineItem.georecency?.circles);
        },
        "georecency.duration": (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "georecency.duration")) {
                return undefined;
            }
            return validateGeorecencyDuration(lineItem.georecency?.circles, lineItem.georecency?.duration);
        },
        confirmedHealthcare: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "confirmedHealthcare")) {
                return undefined;
            }
            return validateGeoConfirmation(lineItem.georecency?.circles, lineItem.confirmedHealthcare, GEO_CONFIRMATION_MESSAGES.confirmedHealthcare);
        },
        confirmedDataUsage: (lineItem) => {
            if (!shouldValidateOnLineItem(lineItem, "confirmedDataUsage")) {
                return undefined;
            }
            return validateGeoConfirmation(lineItem.georecency?.circles, lineItem.confirmedDataUsage, GEO_CONFIRMATION_MESSAGES.confirmedDataUsage);
        }
    };
}
//# sourceMappingURL=index.js.map